import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Play, Clock, ListVideo, ArrowRight } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
export function LayerHollywood() {
  return <section className="py-24 bg-solventum-dark" data-id="element-100">
      <div className="max-w-7xl mx-auto px-6" data-id="element-101">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-102">
          <div className="flex items-center gap-3 mb-4" data-id="element-103">
            <div className="bg-solventum-green text-solventum-dark px-4 py-1 rounded-full text-sm font-bold" data-id="element-104">
              LAYER 1
            </div>
            <Sparkles className="w-5 h-5 text-solventum-green" data-id="element-105" />
            <span className="text-solventum-lightTeal/60 text-xs tracking-[0.2em] uppercase" data-id="element-106">
              Mobile · Library opener → Case open
            </span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-107">
            The Masterclass Intro
          </h2>
          <p className="text-xl text-solventum-lightTeal/80 max-w-3xl" data-id="element-108">
            An intro reel opens the library — the faces of the clinicians and
            the name of the concept. From that cinematic title card, the case
            unfolds full-bleed.
          </p>
        </motion.div>

        {/* Mobile mockup stage */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-[#01403A] via-solventum-dark to-[#011A16] py-16 px-6" data-id="element-109">
          <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(5,221,77,0.18) 0%, transparent 55%)'
        }} data-id="element-110" />

          <div className="relative flex flex-col lg:flex-row items-center justify-center gap-8" data-id="element-111">
            {/* Phone A: Library opener */}
            <div className="relative" data-id="element-112">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-white/10 backdrop-blur text-white text-[10px] font-semibold tracking-[0.2em] uppercase rounded-full border border-white/10 z-10 whitespace-nowrap" data-id="element-113">
                Library opener
              </div>
              <PhoneFrame data-id="element-114">
                <LibraryOpenerScreen data-id="element-115" />
              </PhoneFrame>
            </div>

            {/* Transition arrow */}
            <div className="flex flex-col items-center gap-2" data-id="element-116">
              <div className="w-12 h-12 rounded-full bg-solventum-green/15 ring-1 ring-solventum-green/40 flex items-center justify-center" data-id="element-117">
                <ArrowRight className="w-5 h-5 text-solventum-green hidden lg:block" data-id="element-118" />
                <Sparkles className="w-5 h-5 text-solventum-green lg:hidden" data-id="element-119" />
              </div>
            </div>

            {/* Phone B: Case open */}
            <div className="relative" data-id="element-120">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-solventum-green text-solventum-dark text-[10px] font-bold tracking-[0.2em] uppercase rounded-full z-10 whitespace-nowrap" data-id="element-121">
                Case open
              </div>
              <PhoneFrame data-id="element-122">
                <MasterclassIntroScreen data-id="element-123" />
              </PhoneFrame>
            </div>
          </div>

          {/* Bottom annotations */}
          <div className="relative mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-id="element-124">
            <Annotation title="Intro reel" body="A short opening video — the faces of the roster, set to the concept name." data-id="element-125" />
            <Annotation title="A Solventum Original" body="Editorial framing positions the library like a streaming masterclass." data-id="element-126" />
            <Annotation title="Cinematic full-bleed" body="The case takes over after the intro. No nav, no chrome — just presence." data-id="element-127" />
            <Annotation title="One thumb to play" body="Primary CTA sits in the bottom safe zone — chair-side reach." data-id="element-128" />
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-solventum-lightTeal/60 mt-12 italic" data-id="element-129">
          "Oh, that's Dr O'Connor — I want to see how he handled that fracture."
        </motion.p>
      </div>
    </section>;
}
function Annotation({
  title,
  body
}: {
  title: string;
  body: string;
}) {
  return <div className="max-w-[240px]" data-id="element-130">
      <div className="text-solventum-green text-[10px] font-semibold tracking-[0.25em] uppercase mb-2" data-id="element-131">
        {title}
      </div>
      <div className="text-solventum-lightTeal/70 text-sm leading-relaxed" data-id="element-132">
        {body}
      </div>
    </div>;
}
// ── Library Opener — cinematic intro video frame ─────────────────
const rosterPortraits = ['https://cdn.magicpatterns.com/uploads/jua2EXwXGnWWdCboWXiffF/Nyt_Projekt-4.png', 'https://cdn.magicpatterns.com/uploads/3wQBRHcHu4vFgnJM1FH1y1/Nyt_Projekt-3.png', 'https://cdn.magicpatterns.com/uploads/o1GtuzGAsSups18ayvyhSc/Nyt_Projekt-2.png', 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400&auto=format&fit=crop&q=80', 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&auto=format&fit=crop&q=80'];
function LibraryOpenerScreen() {
  return <div className="relative w-full h-full bg-solventum-dark overflow-hidden" data-id="element-133">
      {/* Atmospheric backdrop */}
      <div className="absolute inset-0 opacity-60" style={{
      backgroundImage: 'radial-gradient(circle at 30% 25%, rgba(5,221,77,0.18) 0%, transparent 55%), radial-gradient(circle at 75% 70%, rgba(25,165,145,0.22) 0%, transparent 55%)'
    }} data-id="element-134" />

      {/* Top: Solventum mark + INTRO indicator */}
      <div className="absolute top-3 left-0 right-0 px-5 flex items-center justify-between z-20" data-id="element-135">
        <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="h-3.5 w-auto" data-id="element-136" />
        <div className="flex items-center gap-1.5" data-id="element-137">
          <div className="w-1 h-1 rounded-full bg-solventum-green animate-pulse" data-id="element-138" />
          <span className="text-solventum-green text-[8px] font-bold tracking-[0.25em] uppercase" data-id="element-139">
            Intro
          </span>
        </div>
      </div>

      {/* Floating clinician portraits — like a film montage */}
      <div className="absolute top-[12%] left-0 right-0 flex justify-center items-end -space-x-3 z-10 px-2" data-id="element-140">
        {rosterPortraits.slice(0, 3).map((src, i) => <div key={src} className="relative w-32 h-48 rounded-xl overflow-hidden ring-1 ring-white/15 shadow-2xl" style={{
        transform: `translateY(${i === 1 ? '-14px' : '8px'}) rotate(${i === 0 ? -4 : i === 2 ? 4 : 0}deg)`,
        zIndex: i === 1 ? 2 : 1
      }} data-id="element-141">
            <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${src})`
        }} data-id="element-142" />
            <div className="absolute inset-0 bg-gradient-to-t from-solventum-dark/60 via-transparent to-transparent" data-id="element-143" />
          </div>)}
      </div>

      {/* Title block — editorial, cinematic */}
      <div className="absolute inset-x-0 bottom-24 px-6 z-20 text-center" data-id="element-144">
        <h3 className="font-editorial text-white text-[32px] font-bold leading-[1.05] mb-4" data-id="element-145">
          Clinical
          <br data-id="element-146" />
          Masterclass
          <br data-id="element-147" />
          <span className="text-solventum-green" data-id="element-148">Library</span>
        </h3>
        <p className="text-white/70 text-[13px] leading-snug max-w-[240px] mx-auto" data-id="element-149">
          Real cases. Expert techniques.
          <br data-id="element-150" />
          By clinicians, for clinicians.
        </p>
      </div>

      {/* Primary CTA — bottom safe zone */}
      <div className="absolute inset-x-0 bottom-0 px-5 pb-10 z-20" data-id="element-151">
        <button className="w-full h-11 bg-solventum-green text-solventum-dark rounded-full text-[13px] font-bold flex items-center justify-center gap-2" data-id="element-152">
          <Play className="w-3.5 h-3.5 fill-solventum-dark" data-id="element-153" />
          Watch the case
        </button>
      </div>
    </div>;
}
// ── Case Open Screen (existing) ───────────────────────────────────
function MasterclassIntroScreen() {
  return <div className="relative w-full h-full" data-id="element-154">
      <div className="absolute inset-0 bg-cover bg-center" style={{
      backgroundImage: "url('https://cdn.magicpatterns.com/uploads/gz7YVWCDxrSDdnmGjyyq3Q/Sk%C3%A6rmbillede_2026-05-11_kl._12.05.06.png')"
    }} data-id="element-155" />
      <div className="absolute inset-0 bg-gradient-to-t from-solventum-dark via-solventum-dark/30 to-transparent" data-id="element-156" />
      <div className="absolute inset-0 bg-gradient-to-b from-solventum-dark/40 via-transparent to-transparent" data-id="element-157" />

      {/* Top app bar */}
      <div className="absolute top-3 left-0 right-0 px-5 flex items-center justify-between z-10" data-id="element-158">
        <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="h-3.5 w-auto" data-id="element-159" />
        <div className="text-white/60 text-[9px] tracking-[0.18em] uppercase" data-id="element-160">
          Case 04
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 px-5 pb-10 z-10" data-id="element-161">
        <div className="text-solventum-green text-[9px] font-bold tracking-[0.25em] uppercase mb-2" data-id="element-162">
          Featured Clinician
        </div>
        <h3 className="font-editorial text-white text-[26px] font-bold leading-[1.1] mb-2" data-id="element-163">
          Dr Chris O'Connor
        </h3>
        <p className="text-white/60 text-[10px] leading-snug mb-3" data-id="element-164">
          Restorative dentist · Newcastle
        </p>
        <p className="text-white/85 text-[12px] leading-relaxed mb-4" data-id="element-165">
          Restore form and function in extensive root caries with Solventum™
          Filtek™ Easy Match Flowable Restorative.
        </p>

        <div className="flex items-center gap-3 text-[10px] text-white/60" data-id="element-166">
          <span className="flex items-center gap-1" data-id="element-167">
            <Clock className="w-3 h-3" data-id="element-168" />4 min
          </span>
          <span className="w-1 h-1 rounded-full bg-white/30" data-id="element-169" />
          <span className="flex items-center gap-1" data-id="element-170">
            <ListVideo className="w-3 h-3" data-id="element-171" />8 chapters
          </span>
        </div>
      </div>
    </div>;
}